import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Wrench,
  Calendar,
  Gauge,
  CheckCircle,
  Truck,
  AlertTriangle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  PieChart as ReChartsPie,
  Pie
} from 'recharts';
import api, { initApi } from '../services/api';
import { trucksService } from '../services/trucksService';

// Colores para las gráficas de pastel
const PIE_COLORS = ['#3b82f6', '#10b981', '#f97316', '#f59e0b', '#eab308', '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6', '#f43f5e'];

const FleetStatusPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [fleet, setFleet] = useState([]);
  const [stats, setStats] = useState({});
  const [truckTypes, setTruckTypes] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [displayedStatusHistory, setDisplayedStatusHistory] = useState([]);
  const [statusHistorySource, setStatusHistorySource] = useState('unknown'); // 'api' | 'api-50001' | 'fallback'
  const [maintenanceError, setMaintenanceError] = useState(null);
  const [tractocamionesMarcas, setTractocamionesMarcas] = useState([]);
  const [dollysMarcas, setDollysMarcas] = useState([]);
  const [semiRemolquesMarcas, setSemiRemolquesMarcas] = useState([]);
  const [autoTanquesMarcas, setAutoTanquesMarcas] = useState([]);
  const [cajasSecasMarcas, setCajasSecasMarcas] = useState([]);
  const [unidadesGasMarcas, setUnidadesGasMarcas] = useState([]);
  const [gruasMarcas, setGruasMarcas] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Cargar datos iniciales (flota, resumen y historial)
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);

        // Intentar obtener resumen de la flotilla (por tipo)
        const summaryResp = await trucksService.getFleetSummary().catch(err => {
          console.warn('No se obtuvo fleetSummary desde el servicio:', err && err.message ? err.message : err);
          return null;
        });

        // Obtener lista completa de vehículos (fallback si summary no es útil)
        // Pedir un límite alto para evitar paginación por defecto y obtener todas las unidades
        const allResp = await trucksService.getAll({ limit: 1000 }).catch(err => {
          console.warn('No se pudieron obtener trucks:', err && err.message ? err.message : err);
          return null;
        });

        // Obtener historial de estados
        const history = await loadStatusHistory().catch(err => {
          console.warn('Error cargando statusHistory:', err && err.message ? err.message : err);
          return [];
        });

        if (!mounted) return;

        if (allResp && allResp.data && Array.isArray(allResp.data)) {
          setFleet(allResp.data);
        } else if (allResp && Array.isArray(allResp)) {
          setFleet(allResp);
        } else {
          setFleet([]);
        }

        if (summaryResp && summaryResp.success && summaryResp.data) {
          setStats({ fleetSummary: summaryResp.data.byType ? { byType: summaryResp.data.byType, totals: summaryResp.data.totals } : null });
          const types = Object.keys(summaryResp.data.byType || {});
          setTruckTypes(types.map(t => mapNormalizedToDisplay(normalizeString(t).replace(/\s+/g, ''))));
        } else {
          // Si no hay resumen, intentar calcular algunos stats básicos desde la lista
          setStats({});
          setTruckTypes([]);
        }

        // Normalize history into months with the specific keys we need
        const normalizeStatusHistory = (raw = []) => {
          // raw expected to be array of objects per month
          return (raw || []).map(entry => {
            const month = entry.month || entry.mes || entry.name || '';
            const active = Number(entry.active ?? entry.activo ?? entry.A ?? 0) || 0;
            // Prefer explicit fields if available
            const preventive = Number(entry.mantenimiento_preventivo ?? entry.preventivo ?? entry.maintenance_preventive ?? 0) || 0;
            const corrective = Number(entry.mantenimiento_correctivo ?? entry.correctivo ?? entry.maintenance_corrective ?? 0) || 0;
            // If backend only provides a generic 'maintenance' value, split 60/40
            const maintenanceGeneric = Number(entry.maintenance ?? entry.mantenimiento ?? 0) || 0;
            const maintPrevent = preventive > 0 || corrective > 0 ? preventive : Math.ceil(maintenanceGeneric * 0.6);
            const maintCorrect = preventive > 0 || corrective > 0 ? corrective : (maintenanceGeneric - maintPrevent);
            const baja = Number(entry.baja ?? entry.decommissioned ?? entry.decom ?? 0) || 0;

            return {
              month: month,
              active: active,
              maintenance_preventive: maintPrevent,
              maintenance_corrective: maintCorrect,
              baja: baja
            };
          });
        };

        setStatusHistory(normalizeStatusHistory(history || []));
  // Initialize displayedStatusHistory to the loaded global history
  setDisplayedStatusHistory(normalizeStatusHistory(history || []));
      } catch (err) {
        console.error('Error en loadData:', err);
        setError(err.message || String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => { mounted = false; };
  }, []);

  // Cargar marcas de tractocamiones cuando se selecciona el filtro
  useEffect(() => {
    const loadMarcas = async () => {
      try {
        if (activeFilter === 'Tracto Camión' || activeFilter === 'all') {
          const response = await trucksService.getTractocamionesMarcas();
          if (response.success && response.data) {
            setTractocamionesMarcas(response.data);
          }
        }
        
        if (activeFilter === 'Dolly' || activeFilter === 'all') {
          const response = await trucksService.getDollysMarcas();
          if (response.success && response.data) {
            setDollysMarcas(response.data);
          }
        }
        
        if (activeFilter === 'Semi Remolque' || activeFilter === 'all') {
          const response = await trucksService.getSemiRemolquesMarcas();
          if (response.success && response.data) {
            setSemiRemolquesMarcas(response.data);
          }
        }
        
        if (activeFilter === 'Auto Tanque' || activeFilter === 'all') {
          const response = await trucksService.getAutoTanquesMarcas();
          if (response.success && response.data) {
            setAutoTanquesMarcas(response.data);
          }
        }
        
        if (activeFilter === 'Caja Seca' || activeFilter === 'all') {
          const response = await trucksService.getCajasSecasMarcas();
          if (response.success && response.data) {
            setCajasSecasMarcas(response.data);
          }
        }
        
        if (activeFilter === 'Unidad de Gas' || activeFilter === 'all') {
          const response = await trucksService.getUnidadesGasMarcas();
          if (response.success && response.data) {
            setUnidadesGasMarcas(response.data);
          }
        }
        
        if (activeFilter === 'Grúa' || activeFilter === 'all') {
          const response = await trucksService.getGruasMarcas();
          if (response.success && response.data) {
            setGruasMarcas(response.data);
          }
        }
      } catch (error) {
        console.error('Error cargando marcas:', error);
      }
    };
    loadMarcas();
  }, [activeFilter]);

  // Función para formatear números grandes
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Función para formatear dinero
  const formatMoney = (amount) => {
    if (!amount) return '$0';
    
    const number = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(number)) return '$0';
    
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(number);
  };

  // Normalizar y extraer categoría/tipo de un vehículo
  const normalizeString = (str) => {
    if (!str && str !== 0) return '';
    return String(str)
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .toLowerCase()
      .trim();
  };

  const mapNormalizedToDisplay = (norm) => {
    if (!norm) return 'Sin categoría';
    const m = {
      'tractocamion': 'Tracto Camión',
      'tracto': 'Tracto Camión',
      'cajaseca': 'Caja Seca',
      'caja': 'Caja Seca',
      'dolly': 'Dolly',
      'semiremolque': 'Semi Remolque',
      'semi': 'Semi Remolque',
      'remolque': 'Semi Remolque',
      'autotanque': 'Auto Tanque',
      'tanque': 'Auto Tanque',
      'camioneta': 'Unidad de Gas',
      'unidaddegas': 'Unidad de Gas',
      'unidadgas': 'Unidad de Gas',
      'gas': 'Unidad de Gas',
      'grua': 'Grúa'
    };
    
    // Buscar coincidencia exacta primero
    if (m[norm]) return m[norm];

    // Si no hay coincidencia exacta, buscar por inclusión
    for (const [key, value] of Object.entries(m)) {
      if (norm.includes(key)) {
        console.log(`Categoría normalizada: ${norm} -> ${value}`);
        return value;
      }
    }

    // Title-case fallback
    return norm.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const normalizeCategory = (vehicle) => {
    if (!vehicle) return 'Sin categoría';
    const candidates = [
      vehicle.category,
      vehicle.type,
      vehicle.tipo_unidad,
      vehicle.tipoUnidad,
      vehicle.tipo,
      vehicle.tipoUnidadDescripcion
    ];
    
    // Primero buscar campos específicos
    for (const c of candidates) {
      if (c !== undefined && c !== null && String(c).trim() !== '') {
        const norm = normalizeString(c).replace(/\s+/g, '');
        return mapNormalizedToDisplay(norm);
      }
    }
    
    // Si no se encontró un campo específico, buscar en todo el objeto
    // Esto puede ayudar a encontrar campos que no estén en la lista de candidatos
    for (const key in vehicle) {
      if (typeof vehicle[key] === 'string' && 
          (key.toLowerCase().includes('tipo') || key.toLowerCase().includes('category'))) {
        const norm = normalizeString(vehicle[key]).replace(/\s+/g, '');
        const result = mapNormalizedToDisplay(norm);
        console.log(`Categoría encontrada en campo alternativo ${key}: ${result}`);
        return result;
      }
    }
    
    // Si no se encontró ninguna categoría, usar número económico para inferirla
    if (vehicle.numero_economico) {
      const num = vehicle.numero_economico.toString();
      if (num.startsWith('T')) {
        return 'Tracto Camión';
      } else if (num.startsWith('D')) {
        return 'Dolly';
      } else if (num.startsWith('S')) {
        return 'Semi Remolque';
      } else if (num.startsWith('A')) {
        return 'Auto Tanque';
      } else if (num.startsWith('C')) {
        return 'Caja Seca';
      } else if (num.startsWith('U') || num.startsWith('G')) {
        return 'Unidad de Gas';
      } else if (num.startsWith('GR')) {
        return 'Grúa';
      }
    }
    
    return 'Sin categoría';
  };

  // Devuelve true si el vehículo está en mantenimiento preventivo o correctivo
  const isMaintenance = (vehicle) => {
    if (!vehicle) return false;
    const st = (vehicle.status || vehicle.estatus || '').toString().toLowerCase();
    if (!st) return false;

    const keywords = [
      // Spanish full words
      'mantenimiento preventivo', 'mantenimiento correctivo',
      'preventivo', 'correctivo',
      // Abbreviations and UI variants
      'm. prevent', 'm.prevent', 'm. preventivo', 'm.preventivo',
      'm. correct', 'm.correct', 'm. correctivo', 'm.correctivo',
      // English variants (in case some data uses them)
      'preventive', 'prevent', 'corrective', 'correct'
    ];

    return keywords.some(k => st.indexOf(k) !== -1);
  };

  // Función para cargar historial de estados desde el endpoint de mantenimientos
  const loadStatusHistory = async (year = null) => {
    setMaintenanceError(null);
    
    try {
      // Usar axios con el token de autenticación incluido automáticamente
      const params = year ? { year } : {};
      const response = await api.get('/maintenances/status-history', { params });
      
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setStatusHistorySource('api-maintenances');
        if (response.data.stats) {
          setStats(prev => ({ ...prev, mantenimientos: response.data.stats }));
        }
        return response.data.data;
      }

      if (Array.isArray(response.data)) {
        setStatusHistorySource('api-maintenances-old');
        return response.data;
      }

      console.warn('Respuesta inesperada del servidor:', response.data);
      setStatusHistorySource('no-backend');
      setMaintenanceError('Formato de respuesta inesperado del servidor.');
      return [];
    } catch (err) {
      console.error('Error al obtener historial de estado:', err);
      setStatusHistorySource('no-backend');
      
      if (err.response?.status === 401) {
        setMaintenanceError('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else {
        setMaintenanceError('No se pudo obtener datos del backend. Revisa que el servidor esté funcionando.');
      }
      
      return [];
    }
  };

  const fleetByStatus = {};
  fleet.forEach(vehicle => {
    fleetByStatus[vehicle.status] = (fleetByStatus[vehicle.status] || 0) + 1;
  });

  let statusData = Object.entries(fleetByStatus).map(([name, value]) => ({ name, value }));

  // Preferir resumen por tipo si está disponible
  if (stats && stats.fleetSummary && stats.fleetSummary.byType) {
    const byType = stats.fleetSummary.byType;
    const statusCounts = {};
    Object.values(byType).forEach(v => {
      statusCounts['Activo'] = (statusCounts['Activo'] || 0) + (Number(v.activo || 0));
      statusCounts['M. Preventivo'] = (statusCounts['M. Preventivo'] || 0) + (Number(v.mantenimiento_preventivo || 0));
      statusCounts['M. Correctivo'] = (statusCounts['M. Correctivo'] || 0) + (Number(v.mantenimiento_correctivo || 0));
      statusCounts['Baja'] = (statusCounts['Baja'] || 0) + (Number(v.baja || 0));
    });
    statusData = Object.entries(statusCounts).map(([name, value]) => ({ name, value: Number(value) }));
  }

  // Colores por defecto para las gráficas (evita referencias indefinidas)
  const COLORS = ['#3b82f6', '#10b981', '#f97316', '#f59e0b', '#ef4444', '#b91c1c', '#06b6d4', '#ec4899'];

  // Construir un historial mensual simulado a partir de conteos actuales
  const buildMonthlyHistoryFromCounts = (countsObj = {}) => {
    // countsObj puede venir en formato { mantenimiento_preventivo, mantenimiento_correctivo } o dentro de { data: {...} }
    const counts = countsObj.data || countsObj;
    const preventive = Number(counts.mantenimiento_preventivo || counts.preventivo || counts.total_preventivos || 0);
    const corrective = Number(counts.mantenimiento_correctivo || counts.correctivo || counts.total_correctivos || 0);

    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const currentMonth = new Date().getMonth();
    const history = [];

    for (let i = 0; i < 9; i++) {
      const monthIndex = (currentMonth - 8 + i + 12) % 12;

      // Aplicar variación aleatoria pequeña para dar sensación histórica
      const variationPrevent = Math.max(0.6, Math.min(1.4, 0.9 + Math.random() * 0.5));
      const variationCorrect = Math.max(0.6, Math.min(1.4, 0.9 + Math.random() * 0.5));

      history.push({
        month: months[monthIndex],
        maintenance_preventive: Math.round(preventive * variationPrevent),
        maintenance_corrective: Math.round(corrective * variationCorrect)
      });
    }

    return history;
  };

  // Cuando cambie el filtro activo, actualizar la gráfica de mantenimientos para reflejar el tipo seleccionado
  useEffect(() => {
    let mounted = true;

    const updateDisplayedHistory = async () => {
      if (activeFilter === 'all') {
        setDisplayedStatusHistory(statusHistory);
        return;
      }

      // Mapear el nombre de UI al tipo esperado por la API
      const displayToApiType = {
        'Tracto Camión': 'Tractocamion',
        'Dolly': 'Dolly',
        'Semi Remolque': 'Semi Remolque',
        'Auto Tanque': 'Auto Tanque',
        'Caja Seca': 'Caja Seca',
        'Unidad de Gas': 'Unidad De Gasolina',
        'Grúa': 'Grua'
      };

      const apiType = displayToApiType[activeFilter] || activeFilter;

      try {
        // Intentar obtener historial real por tipo desde el endpoint de mantenimientos
        const resp = await trucksService.getStatusHistoryByType(apiType).catch(() => null);

        if (!mounted) return;

        if (resp && resp.success && Array.isArray(resp.data) && resp.data.length > 0) {
          // Normalizar estructura (asegurar month, maintenance_preventive, maintenance_corrective)
          const normalized = (resp.data || []).map(entry => ({
            month: entry.month || entry.mes || entry.month_key || entry.name || '',
            maintenance_preventive: Number(entry.maintenance_preventive ?? entry.mantenimiento_preventivo ?? entry.preventivo ?? 0) || 0,
            maintenance_corrective: Number(entry.maintenance_corrective ?? entry.mantenimiento_correctivo ?? entry.correctivo ?? 0) || 0
          }));

          setDisplayedStatusHistory(normalized);

          // Actualizar stats de mantenimientos si el endpoint devuelve estadísticas
          if (resp.stats) setStats(prev => ({ ...prev, mantenimientos: resp.stats }));
          return;
        }

        // Si no hay datos reales del backend para este tipo, mostrar vacío (el usuario pidió que no se simule)
        setDisplayedStatusHistory([]);
      } catch (err) {
        console.warn('Error actualizando historial por filtro:', err);
        setDisplayedStatusHistory([]);
      }
    };

    updateDisplayedHistory();

    return () => { mounted = false; };
  }, [activeFilter, statusHistory, fleet]);

  // Recargar datos cuando cambie el año seleccionado
  useEffect(() => {
    let mounted = true;

    const reloadHistoryForYear = async () => {
      try {
        const history = await loadStatusHistory(selectedYear);
        if (!mounted) return;
        
        const normalizeStatusHistory = (raw = []) => {
          return (raw || []).map(entry => {
            const month = entry.month || entry.mes || entry.name || '';
            const preventive = Number(entry.mantenimiento_preventivo ?? entry.preventivo ?? entry.maintenance_preventive ?? 0) || 0;
            const corrective = Number(entry.mantenimiento_correctivo ?? entry.correctivo ?? entry.maintenance_corrective ?? 0) || 0;
            
            return {
              month: month,
              maintenance_preventive: preventive,
              maintenance_corrective: corrective
            };
          });
        };

        const normalized = normalizeStatusHistory(history);
        setStatusHistory(normalized);
        
        // Si no hay filtro activo, actualizar displayedStatusHistory también
        if (activeFilter === 'all') {
          setDisplayedStatusHistory(normalized);
        }
      } catch (err) {
        console.warn('Error recargando historial para año:', selectedYear, err);
      }
    };

    reloadHistoryForYear();

    return () => { mounted = false; };
  }, [selectedYear]);

  // Construir los datos de categoría (para el Pie) de forma defensiva
  let categoryData = [];
  try {
    if (stats && stats.fleetSummary && stats.fleetSummary.byType) {
      const byType = stats.fleetSummary.byType;
      const mapApiTypeToDisplay = (key) => mapNormalizedToDisplay(normalizeString(key).replace(/\s+/g, ''));
      categoryData = Object.entries(byType)
        .map(([k, v]) => ({ name: mapApiTypeToDisplay(k) || k, value: Number(v.total || 0) }))
        .filter(d => d.value > 0 && d.name !== 'Baja');
    } else {
      // Fallback: contar desde `fleet`
      const tmp = {};
      (fleet || []).forEach(v => {
        const c = normalizeCategory(v) || 'Sin categoría';
        if (c === 'Baja') return; // excluir bajas de la distribución por tipo
        tmp[c] = (tmp[c] || 0) + 1;
      });
      categoryData = Object.entries(tmp).map(([name, value]) => ({ name, value }));
    }
  } catch (e) {
    console.warn('Error construyendo categoryData:', e);
    categoryData = [];
  }

  // Si hay un filtro activo (por tipo), preferir los datos por tipo desde stats
  if (activeFilter !== 'all') {
    // Mapear nombres de UI a los tipos usados por fleetSummary/getFleetSummary
    const displayToApiType = {
      'Tracto Camión': 'Tractocamion',
      'Dolly': 'Dolly',
      'Semi Remolque': 'Semi Remolque',
      'Auto Tanque': 'Auto Tanque',
      'Caja Seca': 'Caja Seca',
      'Unidad de Gas': 'Unidad De Gasolina',
      'Grúa': 'Grua'
    };

    const apiType = displayToApiType[activeFilter] || activeFilter;

    // 1) Si fleetSummary tiene los datos por tipo, usarlos directamente
    if (stats && stats.fleetSummary && stats.fleetSummary.byType && stats.fleetSummary.byType[apiType]) {
      const v = stats.fleetSummary.byType[apiType];
      statusData = [
        { name: 'Activo', value: Number(v.activo || 0) },
        { name: 'M. Preventivo', value: Number(v.mantenimiento_preventivo || 0) },
        { name: 'M. Correctivo', value: Number(v.mantenimiento_correctivo || 0) },
        { name: 'Mantenimiento', value: Number(v.mantenimiento_generico || 0) },
        { name: 'Baja', value: Number(v.baja || 0) }
      ].filter(item => item.value > 0); // Solo mostrar categorías con valores > 0

    // 2) Si trucksService.getStatusCountsByType ya pobló stats[activeFilter] como array, usarlo
    } else if (stats && Array.isArray(stats[activeFilter]) && stats[activeFilter].length > 0) {
      statusData = stats[activeFilter].map(item => ({ name: item.name, value: Number(item.value || 0) }));

    // 3) Fallback: contar directamente desde el estado `fleet` filtrado por la categoría mostrada
    } else {
      const fleetOfType = (fleet || []).filter(vehicle => normalizeCategory(vehicle) === activeFilter);
      const counts = {};
      fleetOfType.forEach(t => {
        const est = (t.status || t.estatus || '').toString().toLowerCase();
        if (est.includes('activo')) counts['Activo'] = (counts['Activo'] || 0) + 1;
        else if (est.includes('preventivo')) counts['M. Preventivo'] = (counts['M. Preventivo'] || 0) + 1;
        else if (est.includes('correctivo')) counts['M. Correctivo'] = (counts['M. Correctivo'] || 0) + 1;
        else if (est.includes('baja')) counts['Baja'] = (counts['Baja'] || 0) + 1;
        else if (est.includes('mantenim')) counts['Mantenimiento'] = (counts['Mantenimiento'] || 0) + 1;
        else counts['Otro'] = (counts['Otro'] || 0) + 1;
      });
      statusData = Object.entries(counts).map(([name, value]) => ({ name, value: Number(value) })).filter(item => item.value > 0);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
     );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-red-500 text-xl mb-4">⚠️ Error</div>
        <p className="text-gray-700">{error}</p>
        <button 
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => window.location.reload()}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Estado de la Flotilla</h1>
        <button 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
          onClick={() => navigate('/fleet/new')}
        >
          <span>+ Nuevo Vehículo</span>
        </button>
      </div>

      

      {/* Filtros por categoría */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <button 
          className={`px-3 py-1 rounded-full text-sm ${activeFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('all')}
        >
          Todos
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${activeFilter === 'Tracto Camión' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('Tracto Camión')}
        >
          <img src="/images/tractocamion.png" alt="Tractocamión" className="w-4 h-4 object-contain" />
          Tractocamiones
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${activeFilter === 'Dolly' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('Dolly')}
        >
          <img src="/images/dolly.png" alt="Dolly" className="w-4 h-4 object-contain" />
          Dollys
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${activeFilter === 'Semi Remolque' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('Semi Remolque')}
        >
          <img src="/images/semiremolque.png" alt="Semiremolque" className="w-4 h-4 object-contain" />
          Semiremolques
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${activeFilter === 'Auto Tanque' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('Auto Tanque')}
        >
          <img src="/images/auto_tanque.png" alt="Auto Tanque" className="w-4 h-4 object-contain" />
          Auto Tanques
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${activeFilter === 'Caja Seca' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('Caja Seca')}
        >
          <img src="/images/caja_seca.png" alt="Caja Seca" className="w-4 h-4 object-contain" />
          Cajas Secas
        </button>
        {/* Camionetas se integran en Unidades Gas; botón eliminado */}
        <button 
          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${activeFilter === 'Unidad de Gas' ? 'bg-cyan-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('Unidad de Gas')}
        >
          <img src="/images/unidadgas.png" alt="Unidad Gas" className="w-4 h-4 object-contain" />
          Unidades Gas
        </button>
        <button 
          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${activeFilter === 'Grúa' ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => setActiveFilter('Grúa')}
        >
          <img src="/images/grua.png" alt="Grúa" className="w-4 h-4 object-contain" />
          Grúas
        </button>
      </div>
      
      {/* Resumen General - tarjetas compactas (debajo de los filtros) */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Resumen General</h4>
          <div className="text-sm text-gray-600">
            {activeFilter === 'all' ? (
              stats && stats.fleetSummary && stats.fleetSummary.totals ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  <div className="p-3 border rounded">
                    <div className="text-xs text-gray-500">Total unidades</div>
                    <div className="text-lg font-semibold">{stats.fleetSummary.totals.total || 0}</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-xs text-gray-500">Activos</div>
                    <div className="text-lg font-semibold">{stats.fleetSummary.totals.activo || 0}</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-xs text-gray-500">M. Preventivo</div>
                    <div className="text-lg font-semibold">{stats.fleetSummary.totals.mantenimiento_preventivo || 0}</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-xs text-gray-500">M. Correctivo</div>
                    <div className="text-lg font-semibold">{stats.fleetSummary.totals.mantenimiento_correctivo || 0}</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-xs text-gray-500">Bajas</div>
                    <div className="text-lg font-semibold">{stats.fleetSummary.totals.baja || 0}</div>
                  </div>
                </div>
              ) : (
                <div>Datos de resumen no disponibles</div>
              )
            ) : (
              (() => {
                const displayToApiType = {
                  'Tracto Camión': 'Tractocamion',
                  'Dolly': 'Dolly',
                  'Semi Remolque': 'Semi Remolque',
                  'Auto Tanque': 'Auto Tanque',
                  'Caja Seca': 'Caja Seca',
                  'Unidad de Gas': 'Unidad De Gasolina',
                  'Grúa': 'Grua'
                };
                const apiType = displayToApiType[activeFilter] || activeFilter;
                let byType = stats && stats.fleetSummary && stats.fleetSummary.byType ? stats.fleetSummary.byType[apiType] : null;

                if (!byType) {
                  const items = (fleet || []).filter(v => normalizeCategory(v) === activeFilter);
                  const counts = { total: 0, activo: 0, mantenimiento_preventivo: 0, mantenimiento_correctivo: 0, baja: 0 };
                  items.forEach(t => {
                    counts.total++;
                    const est = (t.status || t.estatus || '').toString().toLowerCase();
                    if (est.includes('activo')) counts.activo++;
                    else if (est.includes('preventivo')) counts.mantenimiento_preventivo++;
                    else if (est.includes('correctivo')) counts.mantenimiento_correctivo++;
                    else if (est.includes('baja')) counts.baja++;
                  });
                  byType = counts;
                }

                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    <div className="p-3 border rounded">
                      <div className="text-xs text-gray-500">Tipo</div>
                      <div className="text-lg font-semibold">{activeFilter}</div>
                    </div>
                    <div className="p-3 border rounded">
                      <div className="text-xs text-gray-500">Activos</div>
                      <div className="text-lg font-semibold">{byType.activo || 0}</div>
                    </div>
                    <div className="p-3 border rounded">
                      <div className="text-xs text-gray-500">M. Preventivo</div>
                      <div className="text-lg font-semibold">{byType.mantenimiento_preventivo || 0}</div>
                    </div>
                    <div className="p-3 border rounded">
                      <div className="text-xs text-gray-500">M. Correctivo</div>
                      <div className="text-lg font-semibold">{byType.mantenimiento_correctivo || 0}</div>
                    </div>
                    <div className="p-3 border rounded">
                      <div className="text-xs text-gray-500">Bajas</div>
                      <div className="text-lg font-semibold">{byType.baja || 0}</div>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </div>

      {/* Totales por Tipo: visible solo en 'Todos' */}
      {activeFilter === 'all' && (
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Totales por Tipo</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-700">
              {(() => {
                const byType = (stats && stats.fleetSummary && stats.fleetSummary.byType) || {};
                const knownTypes = ['Tractocamion','Dolly','Semi Remolque','Auto Tanque','Caja Seca','Unidad De Gasolina','Grua'];
                const computed = Object.keys(byType).length === 0 ? (() => {
                  const map = {};
                  (fleet || []).forEach(v => {
                    const key = (v.type || v.category || '').toString();
                    map[key] = map[key] || { total: 0, activo: 0, mantenimiento_preventivo: 0, mantenimiento_correctivo: 0, baja: 0 };
                    map[key].total++;
                    const est = (v.status || v.estatus || '').toLowerCase();
                    if (est.includes('activo')) map[key].activo++;
                    else if (est.includes('preventivo')) map[key].mantenimiento_preventivo++;
                    else if (est.includes('correctivo')) map[key].mantenimiento_correctivo++;
                    else if (est.includes('baja')) map[key].baja++;
                  });
                  return map;
                })() : byType;

                const entries = knownTypes.map(t => [t, computed[t] || { total: 0, activo: 0, mantenimiento_preventivo: 0, mantenimiento_correctivo: 0, baja: 0 }]);

                return entries.map(([typeKey, val]) => (
                  <div key={`total-type-${typeKey}`} className="p-2 border rounded">
                    <div className="text-xs text-gray-500">{typeKey}</div>
                    <div className="text-lg font-semibold">{val.total || 0}</div>
                    <div className="text-xs text-gray-500">A:{val.activo || 0} P:{val.mantenimiento_preventivo || 0} C:{val.mantenimiento_correctivo || 0} B:{val.baja || 0}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Estado de la Flotilla */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 lg:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Estado de la Flotilla
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={statusData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={90}
                tick={{ fontSize: 12 }}
              />
              <Tooltip formatter={(value) => value} />
              <Legend />
              <Bar 
                dataKey="value" 
                name="Cantidad"
              >
                {statusData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={
                      entry.name === 'Activo' ? '#10b981' :
                      entry.name === 'Mantenimiento' ? '#f59e0b' :
                      entry.name === 'M. Preventivo' ? '#f59e0b' :
                      entry.name === 'M. Correctivo' ? '#f97316' :
                      entry.name === 'Reparación' ? '#ef4444' :
                      entry.name === 'Baja' ? '#b91c1c' :
                      entry.name === 'Standby' ? '#3b82f6' :
                      COLORS[index % COLORS.length]
                    } 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

  {/* Distribución por tipo de vehículo en gráfica circular */}
  {(['Tracto Camión','Dolly','Semi Remolque','Auto Tanque','Caja Seca','Unidad de Gas','Grúa'].indexOf(activeFilter) === -1) && (
  <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          Distribución por Tipo de Vehículo
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <ReChartsPie>
            <Pie
              data={categoryData}
              cx="50%"
              cy="50%"
              labelLine={true}
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {categoryData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [value, `${name}`]} />
            <Legend />
          </ReChartsPie>
        </ResponsiveContainer>
  </motion.div>
  )}

      {/* Gráficas Radiales para cada categoría */}
      {activeFilter !== 'all' && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <img 
              src={
                activeFilter === 'Tracto Camión' ? "/images/tractocamion.png" :
                activeFilter === 'Dolly' ? "/images/dolly.png" :
                activeFilter === 'Semi Remolque' ? "/images/semiremolque.png" :
                activeFilter === 'Auto Tanque' ? "/images/auto_tanque.png" :
                activeFilter === 'Caja Seca' ? "/images/caja_seca.png" :
                activeFilter === 'Unidad de Gas' ? "/images/unidadgas.png" :
                activeFilter === 'Grúa' ? "/images/grua.png" :
                "/images/tractocamion.png"
              } 
              alt={activeFilter} 
              className="w-8 h-8 object-contain" 
            />
            <h3 className="text-lg font-semibold text-gray-800">Estado de {activeFilter}</h3>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart 
              outerRadius={150}
              width={500}
              height={300}
              data={ activeFilter === 'all' ? (stats && stats.tractoCamionesStatus ? stats.tractoCamionesStatus : []) : statusData }
            >
              <PolarGrid />
              <PolarAngleAxis dataKey="name" />
              <PolarRadiusAxis angle={30} domain={[0, 'auto']} />
              <Radar 
                name="Unidades" 
                dataKey="value" 
                stroke={
                  activeFilter === 'Tracto Camión' ? '#3b82f6' :
                  activeFilter === 'Dolly' ? '#10b981' :
                  activeFilter === 'Semi Remolque' ? '#f97316' :
                  activeFilter === 'Auto Tanque' ? '#f59e0b' :
                  activeFilter === 'Caja Seca' ? '#eab308' :
                  activeFilter === 'Unidad de Gas' ? '#06b6d4' :
                  activeFilter === 'Grúa' ? '#ec4899' :
                  activeFilter === 'Camioneta' ? '#14b8a6' :
                  '#3b82f6'
                } 
                fill={
                  activeFilter === 'Tracto Camión' ? '#3b82f6' :
                  activeFilter === 'Dolly' ? '#10b981' :
                  activeFilter === 'Semi Remolque' ? '#f97316' :
                  activeFilter === 'Auto Tanque' ? '#f59e0b' :
                  activeFilter === 'Caja Seca' ? '#eab308' :
                  activeFilter === 'Unidad de Gas' ? '#06b6d4' :
                  activeFilter === 'Grúa' ? '#ec4899' :
                  activeFilter === 'Camioneta' ? '#14b8a6' :
                  '#3b82f6'
                } 
                fillOpacity={0.6} 
              />
              <Tooltip formatter={(value) => [`${value} unidades`, 'Cantidad']} />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfica de Pastel - Distribución de Marcas de Tractocamiones */}
      {activeFilter === 'Tracto Camión' && tractocamionesMarcas.length > 0 && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por Marca de Tractocamiones
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ReChartsPie>
              <Pie
                data={tractocamionesMarcas}
                dataKey="cantidad"
                nameKey="marca"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({marca, cantidad, percent}) => `${marca}: ${cantidad} (${(percent * 100).toFixed(1)}%)`}
                labelLine={true}
              >
                {tractocamionesMarcas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} unidades`, name]}
              />
              <Legend />
            </ReChartsPie>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfica de Pastel - Distribución de Marcas de Dollys */}
      {activeFilter === 'Dolly' && dollysMarcas.length > 0 && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por Marca de Dollys
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ReChartsPie>
              <Pie
                data={dollysMarcas}
                dataKey="cantidad"
                nameKey="marca"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({marca, cantidad, percent}) => `${marca}: ${cantidad} (${(percent * 100).toFixed(1)}%)`}
                labelLine={true}
              >
                {dollysMarcas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} unidades`, name]}
              />
              <Legend />
            </ReChartsPie>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfica de Pastel - Distribución de Marcas de Semi Remolques */}
      {activeFilter === 'Semi Remolque' && semiRemolquesMarcas.length > 0 && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por Marca de Semi Remolques
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ReChartsPie>
              <Pie
                data={semiRemolquesMarcas}
                dataKey="cantidad"
                nameKey="marca"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({marca, cantidad, percent}) => `${marca}: ${cantidad} (${(percent * 100).toFixed(1)}%)`}
                labelLine={true}
              >
                {semiRemolquesMarcas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} unidades`, name]}
              />
              <Legend />
            </ReChartsPie>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfica de Pastel - Distribución de Marcas de Auto Tanques */}
      {activeFilter === 'Auto Tanque' && autoTanquesMarcas.length > 0 && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por Marca de Auto Tanques
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ReChartsPie>
              <Pie
                data={autoTanquesMarcas}
                dataKey="cantidad"
                nameKey="marca"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({marca, cantidad, percent}) => `${marca}: ${cantidad} (${(percent * 100).toFixed(1)}%)`}
                labelLine={true}
              >
                {autoTanquesMarcas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} unidades`, name]}
              />
              <Legend />
            </ReChartsPie>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfica de Pastel - Distribución de Marcas de Cajas Secas */}
      {activeFilter === 'Caja Seca' && cajasSecasMarcas.length > 0 && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por Marca de Cajas Secas
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ReChartsPie>
              <Pie
                data={cajasSecasMarcas}
                dataKey="cantidad"
                nameKey="marca"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({marca, cantidad, percent}) => `${marca}: ${cantidad} (${(percent * 100).toFixed(1)}%)`}
                labelLine={true}
              >
                {cajasSecasMarcas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} unidades`, name]}
              />
              <Legend />
            </ReChartsPie>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfica de Pastel - Distribución de Marcas de Unidades de Gasolina */}
      {activeFilter === 'Unidad de Gas' && unidadesGasMarcas.length > 0 && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por Marca de Unidades de Gasolina
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ReChartsPie>
              <Pie
                data={unidadesGasMarcas}
                dataKey="cantidad"
                nameKey="marca"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({marca, cantidad, percent}) => `${marca}: ${cantidad} (${(percent * 100).toFixed(1)}%)`}
                labelLine={true}
              >
                {unidadesGasMarcas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} unidades`, name]}
              />
              <Legend />
            </ReChartsPie>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfica de Pastel - Distribución de Marcas de Grúas */}
      {activeFilter === 'Grúa' && gruasMarcas.length > 0 && (
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución por Marca de Grúas
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ReChartsPie>
              <Pie
                data={gruasMarcas}
                dataKey="cantidad"
                nameKey="marca"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({marca, cantidad, percent}) => `${marca}: ${cantidad} (${(percent * 100).toFixed(1)}%)`}
                labelLine={true}
              >
                {gruasMarcas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} unidades`, name]}
              />
              <Legend />
            </ReChartsPie>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Evolución del estado de la flota */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Mantenimientos por Mes (Preventivos y Correctivos)
          </h3>
          
          {/* Selector de Año */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Año:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Estadísticas de Mantenimiento */}
        {stats && stats.mantenimientos && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 max-w-4xl mx-auto">
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg text-center">
              <div className="text-xs text-gray-600 mb-1">Total Mantenimientos (12 meses)</div>
              <div className="text-2xl font-semibold text-blue-600">{stats.mantenimientos.total_mantenimientos || 0}</div>
            </div>
            <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-center">
              <div className="text-xs text-gray-600 mb-1">Preventivos</div>
              <div className="text-2xl font-semibold text-yellow-600">{stats.mantenimientos.total_preventivos || 0}</div>
            </div>
            <div className="p-4 bg-orange-50 border border-orange-100 rounded-lg text-center">
              <div className="text-xs text-gray-600 mb-1">Correctivos</div>
              <div className="text-2xl font-semibold text-orange-600">{stats.mantenimientos.total_correctivos || 0}</div>
            </div>
          </div>
        )}
        {maintenanceError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded text-sm text-red-700">
            <strong>Error:</strong> {maintenanceError}
            <div className="text-xs text-gray-500">Asegúrate de que el backend esté corriendo y accesible en el puerto configurado.</div>
          </div>
        )}
        
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={displayedStatusHistory}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value, name) => [`${value}`, name]} />
            <Legend />
            <Bar dataKey="maintenance_preventive" name="M. Preventivo" fill="#f59e0b" />
            <Bar dataKey="maintenance_corrective" name="M. Correctivo" fill="#f97316" />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Lista de Vehículos */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <img src="/images/tractocamion.png" alt="Tracto camión" className="w-5 h-5 object-contain" />
          Lista de Vehículos {activeFilter !== 'all' ? `- ${activeFilter}` : ''}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs text-gray-600 uppercase bg-gray-50">
              <tr>
                <th className="px-4 py-3 rounded-tl-lg">ID</th>
                <th className="px-4 py-3">Modelo</th>
                <th className="px-4 py-3">Número económico</th>
                <th className="px-4 py-3">Categoría</th>
                <th className="px-4 py-3">Placas</th>
                <th className="px-4 py-3">Kilometraje</th>
                <th className="px-4 py-3">Último Servicio</th>
                <th className="px-4 py-3">Próximo Servicio</th>
                <th className="px-4 py-3 rounded-tr-lg">Estado</th>
              </tr>
            </thead>
            <tbody>
              {fleet
                .filter(isMaintenance)
                .filter(vehicle => {
                  // If an active filter is set (e.g., 'Tracto Camión'), only show vehicles of that category
                  if (activeFilter && activeFilter !== 'all') {
                    return normalizeCategory(vehicle) === activeFilter;
                  }
                  return true;
                })
                .map((vehicle, index) => (
                <tr 
                  key={`vehicle-${vehicle.id || index}`} 
                  className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/trucks/${vehicle.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/fleet/${vehicle.id}`);
                    }
                  }}
                >
                  <td className="px-4 py-3 font-medium">{vehicle.id}</td>
                  <td className="px-4 py-3">{vehicle.model || vehicle.modelo || vehicle.type}</td>
                  <td className="px-4 py-3">{vehicle.numero_economico}</td>
                  <td className="px-4 py-3">{normalizeCategory(vehicle)}</td>
                  <td className="px-4 py-3">{vehicle.plates}</td>
                  <td className="px-4 py-3">{Number(vehicle.mileage || 0).toLocaleString('es-MX')} km</td>
                  <td className="px-4 py-3">{vehicle.lastService || ''}</td>
                  <td className="px-4 py-3">{vehicle.nextService || ''}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      vehicle.status === 'Activo' ? 'bg-green-100 text-green-800' :
                      vehicle.status === 'Mantenimiento preventivo' || vehicle.status === 'M. Preventivo' ? 'bg-yellow-100 text-yellow-800' :
                      vehicle.status === 'Mantenimiento correctivo' || vehicle.status === 'M. Correctivo' ? 'bg-orange-100 text-orange-800' :
                      vehicle.status === 'Mantenimiento' ? 'bg-yellow-100 text-yellow-800' :
                      vehicle.status === 'Reparación' ? 'bg-red-100 text-red-800' :
                      vehicle.status === 'Baja' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {vehicle.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Estado de mantenimiento próximo */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Wrench className="w-5 h-5 text-orange-600" />
          Próximos Mantenimientos
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs text-gray-600 uppercase bg-gray-50">
              <tr>
                <th className="px-4 py-3 rounded-tl-lg">ID</th>
                <th className="px-4 py-3">Vehículo</th>
                <th className="px-4 py-3">Categoría</th>
                <th className="px-4 py-3">Placas</th>
                <th className="px-4 py-3">Fecha Próximo Servicio</th>
                <th className="px-4 py-3">Días Restantes</th>
                <th className="px-4 py-3 rounded-tr-lg">Estado</th>
              </tr>
            </thead>
            <tbody>
              {fleet
                .filter(v => v.nextService) // Solo vehículos con fecha de próximo servicio
                .sort((a, b) => new Date(a.nextService) - new Date(b.nextService)) // Ordenar por fecha más próxima
                .slice(0, 5) // Mostrar solo los 5 más próximos
                .map((vehicle, index) => {
                  const daysRemaining = Math.ceil((new Date(vehicle.nextService) - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <tr 
                      key={`next-service-${vehicle.id || index}`} 
                      className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/trucks/${vehicle.id}`)}
                    >
                      <td className="px-4 py-3 font-medium">{vehicle.id}</td>
                      <td className="px-4 py-3">{vehicle.type}</td>
                      <td className="px-4 py-3">{normalizeCategory(vehicle)}</td>
                      <td className="px-4 py-3">{vehicle.plates}</td>
                      <td className="px-4 py-3">{vehicle.nextService}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          daysRemaining < 0 ? 'bg-red-100 text-red-800' :
                          daysRemaining < 7 ? 'bg-orange-100 text-orange-800' :
                          daysRemaining < 15 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {daysRemaining < 0 ? `${Math.abs(daysRemaining)} días atrasado` : 
                           daysRemaining === 0 ? 'Hoy' : 
                           `${daysRemaining} días`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          vehicle.status === 'Activo' ? 'bg-green-100 text-green-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {vehicle.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          {fleet.filter(v => v.nextService).length === 0 && (
            <div className="text-center py-4 text-gray-500 flex items-center justify-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              No hay fechas de mantenimiento programadas
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default FleetStatusPage;